import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLearner } from '@/state/learnerStore';
import { composeGreeting } from '@/lib/dashboard/greeting';
import { estimateSpokenDuration } from '@/lib/codebaby/duration';
import { computeScorecard } from '@/lib/dashboard/scorecard';
import { appHref } from '@/lib/nav';
import { MODULES_SEED, findLesson, type ModuleMeta } from '@/seed/modules';
import { PERSONAS_SEED, PERSONA_ORDER, type PersonaMeta } from '@/seed/personas';
import { SCENARIOS_BY_PERSONA } from '@/seed/scenarios';
import { VERTICALS_SEED, VERTICAL_ORDER, type Vertical } from '@/seed/verticals';
import { AVATAR_IDS } from '@/constants/avatars';
import { COACH } from '@/constants/coach';
import AvatarMount, { type AvatarHandle } from '@/components/avatar/AvatarMount';
import { buildGreetingStartData } from '@/lib/codebaby/startData';
import type { LearnerState } from '@/types/learner';
import type { SessionRecord } from '@/types/session';
import type { PersonaKey } from '@/constants/avatars';
import './DashboardScreen.css';
import './screens.css';

export default function DashboardScreen() {
  const learner = useLearner();
  return (
    <div className="screen screen--dashboard">
      <CoachCard learner={learner} />
      <ScorecardStrip learner={learner} />
      {/* Verticals come first so the breadth of the framework is the
          headline at-a-glance — the coach's 2026-04-29 framing was "every rep
          becomes the vertical expert." QSR is wired with live content for
          this demo; the others are configured/ready-to-activate. */}
      <VerticalsSection />
      {/* Practice leads — the coach's 2026-04-29 feedback put the
          verbal Linda role-play as the headline experience for the leader
          demo. Lessons sit below as the "homework" track. */}
      <PersonaTiles learner={learner} />
      <ResumeCard learner={learner} />
      <LearningPath learner={learner} />
      <PastSessions learner={learner} />
    </div>
  );
}

/* --------------------------- Coach card --------------------------- */

function CoachCard({ learner }: { learner: LearnerState }) {
  const g = composeGreeting(learner);
  // The headline persona = the first unlocked persona (data-driven, never a
  // hardcoded name) — drives the "step into the room" CTA.
  const heroKey =
    PERSONA_ORDER.find((k) => learner.personas_unlocked.includes(k)) ?? PERSONA_ORDER[0]!;
  const heroPersona = PERSONAS_SEED[heroKey];
  const heroScenario = SCENARIOS_BY_PERSONA[heroKey];
  const startData = useMemo(() => buildGreetingStartData(learner), [learner]);
  const coachRef = useRef<AvatarHandle>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);

  // Tell the coach where the learner is once ready. The primary keeps a
  // single conversation across navigations (singleton session); without this
  // the coach's `startData` is stale and it doesn't know the current screen.
  useEffect(() => {
    if (!ready) return;
    coachRef.current?.notifyContext({
      type: 'navigation to',
      description: `The learner ${learner.display_name} is on their dashboard, viewing the coach card and module list.`,
      url: '/',
    });
  }, [ready, learner.display_name]);

  const onPlay = () => {
    if (!ready || playing) return;
    // Scripted content → playResponse() bypasses the dialog flow's LLM and
    // feeds the exact text to TTS. Per the single-source rule: ask() is for
    // conversational turns (check-ins, roleplay), playResponse() is for lines
    // we authored on the front-end.
    coachRef.current?.playResponse(g.body);
  };

  return (
    <section className="coach-card">
      <div className="coach-card__stage">
        <div className="coach-card__avatar">
          <AvatarMount
            ref={coachRef}
            loaderId={AVATAR_IDS.coach}
            startData={startData}
            ariaLabel={`${COACH.fullName}, coach`}
            onPlayerBuilt={() => setReady(true)}
            onResponse={() => setPlaying(true)}
            onSegmentEnded={() => setPlaying(false)}
          />
        </div>
        <span className="coach-card__play-badge" aria-hidden>
          <PlayIcon />
        </span>
      </div>
      <div className="coach-card__body">
        <p className="eyebrow">{g.eyebrow}</p>
        <h2 className="coach-card__headline">{g.headline}</h2>
        <p className="coach-card__copy">{g.body}</p>
        <div className="coach-card__actions">
          {/* Primary action — drops anyone (rep or leader) straight into the
              headline persona's room. Plain anchor (not Link) because
              coach↔persona transitions force a full page reload (CodeBaby loader
              can't be re-initialized on the same page). */}
          <a
            href={appHref(`/roleplay/${heroScenario.scenario_id}`)}
            className="pill-button pill-button--primary"
          >
            Step into the room — {heroPersona.display_name.split(' ')[0]} is up <ArrowRightIcon />
          </a>
          <button
            type="button"
            className="pill-button pill-button--coach-ghost"
            onClick={onPlay}
            disabled={!ready || playing}
            aria-pressed={playing}
          >
            <PlayIcon />{' '}
            {playing ? 'Playing…' : `Play greeting · ${estimateSpokenDuration(g.body)}`}
          </button>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Scorecard ------------------------------ */

function ScorecardStrip({ learner }: { learner: LearnerState }) {
  const tiles = computeScorecard(learner);
  return (
    <section className="scorecard">
      {tiles.map((t) => (
        <article key={t.label} className={`tile tile--${t.color}`}>
          <p className="tile__label">{t.label}</p>
          <p className="tile__stat">
            <span className="tile__value">{t.value}</span>
            <span className="tile__denom"> of {t.total}</span>
          </p>
          <p className="tile__sub">{t.sub}</p>
        </article>
      ))}
    </section>
  );
}

/* ----------------------------- Verticals ----------------------------- */

function VerticalsSection() {
  return (
    <section>
      <p className="eyebrow">Vertical specialization</p>
      <h3 className="section-head">Every rep becomes the vertical expert.</h3>
      <p className="section-sub">
        QSR is wired with live scenarios for this session. Healthcare, Gov-Ed, Retail, and SMB are
        configured the same way and ready to activate for your team.
      </p>
      <div className="verticals-grid">
        {VERTICAL_ORDER.map((key) => (
          <VerticalCard key={key} vertical={VERTICALS_SEED[key]} />
        ))}
      </div>
    </section>
  );
}

function VerticalCard({ vertical }: { vertical: Vertical }) {
  const isActive = vertical.status === 'active';
  return (
    <article
      className={`vertical-card vertical-card--${vertical.color_key} vertical-card--${vertical.status}`}
    >
      <div className="vertical-card__head">
        <span className={`vertical-card__mark vertical-card__mark--${vertical.color_key}`}>
          {vertical.initials}
        </span>
        <span className={`vertical-card__status vertical-card__status--${vertical.status}`}>
          {isActive ? 'Active' : 'Configured'}
        </span>
      </div>
      <h4 className="vertical-card__name">{vertical.display_name}</h4>
      <p className="vertical-card__desc">{vertical.description}</p>
      <ul className="vertical-card__hooks">
        {vertical.hooks.map((h) => (
          <li key={h} className="vertical-card__hook">
            {h}
          </li>
        ))}
      </ul>
      <div className="vertical-card__counts">
        <span>
          <strong>{vertical.persona_count}</strong> personas
        </span>
        <span>
          <strong>{vertical.scenario_count}</strong> scenarios
        </span>
        <span>
          <strong>{vertical.module_count}</strong> modules
        </span>
        <span>
          <strong>{vertical.lesson_count}</strong> lessons
        </span>
      </div>
      {isActive && (
        <a href={appHref('/practice')} className="vertical-card__cta">
          Browse {vertical.short_label} scenarios <ArrowRightIcon />
        </a>
      )}
    </article>
  );
}

/* ------------------------------- Resume -------------------------------- */

function ResumeCard({ learner }: { learner: LearnerState }) {
  const lesson = findLesson(learner.current_module, learner.current_lesson);
  const pct = Math.round(learner.lesson_progress_pct * 100);
  const minutesRemaining = Math.max(1, Math.ceil((1 - learner.lesson_progress_pct) * 12));
  const title = lesson?.title ?? 'Your next lesson';
  return (
    <section className="resume">
      <div className="resume__meta">
        <p className="resume__eyebrow">
          Module {learner.current_module} · Lesson {learner.current_lesson}
        </p>
        <h2 className="resume__title">{title}</h2>
        <div className="resume__progress">
          <div className="resume__progress-track">
            <div className="resume__progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="resume__progress-label">
            {pct}% · ~{minutesRemaining} min remaining
          </span>
        </div>
      </div>
      <Link
        to={`/lesson/${lesson?.lesson_id ?? 'm1-l4-skepticism-to-scope'}`}
        className="resume__cta"
      >
        Resume <ArrowRightIcon />
      </Link>
    </section>
  );
}

/* ---------------------------- Learning path ---------------------------- */

function LearningPath({ learner }: { learner: LearnerState }) {
  return (
    <section>
      <p className="eyebrow">Your path</p>
      <h3 className="section-head">Five modules to ready</h3>
      <ol className="path">
        {MODULES_SEED.map((m, i) => (
          <li key={m.module} className={`path__step ${stepStateClass(m, learner)}`}>
            <PathNode module={m} learner={learner} />
            {i < MODULES_SEED.length - 1 && <span className="path__line" aria-hidden />}
            <span className="path__label">
              M{m.module} · {m.title}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function stepStateClass(m: ModuleMeta, learner: LearnerState): string {
  if (learner.modules_complete >= m.module) return 'path__step--complete';
  if (learner.current_module === m.module) return 'path__step--current';
  return 'path__step--locked';
}

function PathNode({ module: m, learner }: { module: ModuleMeta; learner: LearnerState }) {
  const complete = learner.modules_complete >= m.module;
  const current = learner.current_module === m.module;
  return (
    <div className="path__node" aria-label={`Module ${m.module}`}>
      <span className="path__circle">
        {complete ? <CheckIcon /> : <span className="path__num">{m.module}</span>}
      </span>
      {current && <span className="path__pulse" aria-hidden />}
    </div>
  );
}

/* ----------------------------- Persona tiles --------------------------- */

function PersonaTiles({ learner }: { learner: LearnerState }) {
  return (
    <section>
      <p className="eyebrow">Step into the room</p>
      <h3 className="section-head">Pick your customer. Earn the meeting.</h3>
      <div className="persona-grid">
        {PERSONA_ORDER.map((key) => (
          <PersonaTile key={key} persona={PERSONAS_SEED[key]} learner={learner} />
        ))}
      </div>
    </section>
  );
}

function PersonaTile({ persona, learner }: { persona: PersonaMeta; learner: LearnerState }) {
  const status = personaStatus(persona.key, learner);
  const scenarioId = `scn-${persona.key}`;
  return (
    <article className={`persona-tile persona-tile--${persona.color_key} persona-tile--${status}`}>
      <div className="persona-tile__top">
        <div
          className={`persona-tile__avatar persona-tile__avatar--${persona.color_key}`}
          aria-hidden
        >
          {persona.display_name
            .split(' ')
            .map((p) => p[0])
            .join('')}
        </div>
        <span className={`persona-tile__badge persona-tile__badge--${status}`}>
          {status === 'mastered' && 'Mastered'}
          {status === 'unlocked' && 'Unlocked'}
          {status === 'locked' && 'Locked'}
        </span>
      </div>
      <h4 className="persona-tile__name">{persona.display_name}</h4>
      <p className="persona-tile__role">{persona.short_label}</p>
      <p className="persona-tile__desc">{persona.role_line}</p>
      {status === 'locked' ? (
        <p className="persona-tile__hint">{persona.unlock_hint}</p>
      ) : (
        // Plain anchor (not Link) — coach↔persona transitions force a full
        // page load because CodeBaby's loader can't be re-initialized on the
        // same page (see reference_codebaby_integration_pitfalls memory).
        <a href={appHref(`/roleplay/${scenarioId}`)} className="persona-tile__cta">
          {status === 'mastered' ? 'Warm back up →' : 'Start attempt →'}
        </a>
      )}
    </article>
  );
}

function personaStatus(key: PersonaKey, learner: LearnerState): 'mastered' | 'unlocked' | 'locked' {
  if (learner.personas_mastered.includes(key)) return 'mastered';
  if (learner.personas_unlocked.includes(key)) return 'unlocked';
  return 'locked';
}

/* ----------------------------- Past sessions --------------------------- */

function PastSessions({ learner }: { learner: LearnerState }) {
  const recent = learner.session_history.slice(0, 3);
  if (recent.length === 0) {
    return (
      <section>
        <p className="eyebrow">Past sessions</p>
        <h3 className="section-head">Your history</h3>
        <div className="past-empty">
          Your first practice session will appear here after you finish Module 1 — {COACH.firstName}{' '}
          will guide you in.
        </div>
      </section>
    );
  }
  return (
    <section>
      <div className="past-head">
        <div>
          <p className="eyebrow">Past sessions</p>
          <h3 className="section-head">What you&apos;ve tried</h3>
        </div>
        <span className="past-viewall" aria-disabled>
          View all
        </span>
      </div>
      <ul className="past-list">
        {recent.map((s) => (
          <PastSessionRow key={s.id} session={s} learner={learner} />
        ))}
      </ul>
    </section>
  );
}

function PastSessionRow({ session, learner }: { session: SessionRecord; learner: LearnerState }) {
  const persona = PERSONAS_SEED[session.persona_id];
  // Stale localStorage from a prior persona-key shape can yield an undefined
  // lookup. Skip the row rather than crash the whole dashboard.
  if (!persona) return null;
  const badgeClass =
    session.score >= 70
      ? 'score-badge--win'
      : session.score >= 50
        ? 'score-badge--warn'
        : 'score-badge--cold';
  const prior = priorAttempt(session, learner);
  const delta = prior ? session.score - prior.score : null;
  const outcomeLabel = session.outcome === 'closed' ? 'Closed' : "Didn't close";
  const durationLabel = formatDuration(session.duration_s);
  return (
    <li className="past-row">
      <span className={`score-badge ${badgeClass}`}>{session.score}</span>
      <div className="past-row__meta">
        <p className="past-row__title">
          {persona.display_name} · attempt {session.attempt_number}
        </p>
        <p className="past-row__sub">
          {outcomeLabel} · {durationLabel} · {formatDate(session.started_at)}
        </p>
      </div>
      {delta !== null && delta !== 0 && (
        <span
          className={`delta-pill delta-pill--${delta > 0 ? 'up' : 'down'}`}
          title="vs previous attempt with this persona"
        >
          {delta > 0 ? `+${delta}` : delta}
        </span>
      )}
      <Link to={`/debrief/${session.id}`} className="past-row__cta">
        Revisit <ArrowRightIcon />
      </Link>
    </li>
  );
}

function priorAttempt(s: SessionRecord, learner: LearnerState): SessionRecord | undefined {
  return learner.session_history.find(
    (x) => x.persona_id === s.persona_id && x.attempt_number === s.attempt_number - 1,
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/* -------------------------------- Icons -------------------------------- */

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12.5l4.5 4.5L20 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
